import {
	Avatar,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormHelperText,
	Tooltip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import setToast from "../../helpers/toaster.helper";
import { BackMobileIcon, CancelIcon, InfoCircleIcon } from "../../icon";
import {
	CustomAttributeDefinitionModel,
	fieldTypeToNumber,
} from "../../models/custom-attribute.model";
import {
	Contact,
	ContactCustomAttribute,
	Group,
	mapToContact,
} from "../../models/conversations";
import * as selector from "../../selectors/selectors";
import { ContactService } from "../../services/ContactService";
import { SettingsService } from "../../services/SettingsService";
import { UtilityService } from "../../services/UtilityService";
import ButtonComponent from "../shared/button/button.component";
import { ToasterTexts, buttonLabels } from "../shared/constants";
import { validateForm } from "../shared/form-validation";
import { ToasterType } from "../shared/toaster/toaster.model";
import DateAndTimePicker from "../shared/datetimepicker/date-timepicker.component";

const DROPDOWN_TYPE = fieldTypeToNumber("DROPDOWN");

const resolveDropdownValue = (storedValue: string | undefined, def: CustomAttributeDefinitionModel): string => {
	const value = storedValue?.trim() ?? "";
	const options = def.OptionsDetail ?? [];
	const matchedOption = options.find(
		(option) => option.id === value || option.label === value
	);
	return matchedOption?.id ?? value;
};

const customAttributesToValues = (customAttributes: ContactCustomAttribute[] = [], definitions: CustomAttributeDefinitionModel[] = []): Record<string, string> => {

	const result: Record<string, string> = {};
	const definitionByReference = new Map(
		definitions.map((def) => [def.Reference, def])
	);

	customAttributes.forEach((attribute) => {
		const def = definitionByReference.get(attribute.reference);
		const value = attribute.value ?? "";

		result[attribute.reference] =
			def?.Type === DROPDOWN_TYPE
				? resolveDropdownValue(value, def)
				: value;
	});
	return result;
};

interface Props {
	show: boolean;
	onClose: (isConfirmed?: boolean) => void;
	Contact?: Contact;
	rowId?: string | null;
	clearSelection?: () => void;
}

const ContactViewComponent = (props: Props) => {
	const dispatch = useDispatch()
	const Groups: Group[] = useSelector(selector.groups)
	const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
	const IsDarkMode: boolean = useSelector(selector.isDarkMode)
	const [title, setTitle] = useState(!!props.Contact?.ID ? "Edit Contact" : "Add Contact")
	const [IsLoading, setIsLoading] = useState<boolean>(false)
	const [groupContactCount, setGroupContactCount] = useState<number>(1)
	const customAttributeFields = useSelector(selector.customAttributeFields);
	const [customAttributesValues, setCustomAttributesValues] = useState<Record<string, string>>({});
	const [errorsCustomAttributes, setErrorsCustomAttributes] = useState<Record<string, string>>({});

	const [contactDetails, setContactDetails] = useState<{
		ID: string,
		FirstName: string,
		LastName: string,
		Email: string,
		PhoneNumber: string,
		Groups?: any[],
		CustomAttributes?: ContactCustomAttribute[],
	}>(
		{
			...(props.Contact || {
				ID: "",
				FirstName: "",
				LastName: "",
				Email: "",
				PhoneNumber: "",
				Groups: [],
			}),
			CustomAttributes: props.Contact?.CustomAttributes,
		}
	)

	const [errors, setErrors] = useState<{
		FirstName: string,
		LastName: string,
		Email: string,
		PhoneNumber: string,
	}>({
		FirstName: "",
		LastName: "",
		Email: "",
		PhoneNumber: "",
	})
	const inputRef = useRef(null)

	useEffect(() => {
		setTitle(!!props.Contact?.ID ? "Edit Contact" : "Create Contact")
	}, [props.Contact?.ID])

	useEffect(() => {
		if (!props.show) return;

		const groups =
			props.Contact?.Groups?.length
				? props.Contact?.Groups
				: props.Contact?.Groups ?? [];

		const contactToUse = {
			...props.Contact,
			Groups: groups,
		};

		setContactDetails({
			ID: contactToUse.ID,
			FirstName: contactToUse.FirstName ?? "",
			LastName: contactToUse.LastName ?? "",
			Email: contactToUse.Email ?? "",
			PhoneNumber: contactToUse.PhoneNumber ?? "",
			Groups: groups,
			CustomAttributes: contactToUse.CustomAttributes,
		});

		setCustomAttributesValues(
			props.Contact?.ID
				? customAttributesToValues(props.Contact?.CustomAttributes, customAttributeFields)
				: {},
		);
	}, [props.show, props.Contact?.ID, customAttributeFields]);

	useEffect(() => {
		SettingsService.getSettingByCode(SettingsService.Group_Contacts_Code).then(res => {
			if (!!res)
				setGroupContactCount(parseInt(res.value) || 0);
		});
	}, []);

	const handleChange = (name: any, value: any, isNotReqired?: boolean) => {
		const input = inputRef.current;
		const cursorPosition = input.selectionStart;
		const previousValue = contactDetails[name] || '';
		if (name === "PhoneNumber") {
			const numericValue = UtilityService.unmaskPhoneNumber(value.replace(/\D/g, ''));
			const previousUnmasked = UtilityService.unmaskPhoneNumber(previousValue);
			if (isNotReqired && numericValue === "") {
				setErrors({ ...errors, [name]: "" });
			} else {
				validateForm({ [name]: numericValue }, (Errors) => {
					setErrors({ ...errors, ...Errors });
				});
			}
			if (numericValue.length <= previousUnmasked.length || /^\d*$/.test(numericValue)) {
				const previousMasked = UtilityService.maskPhoneNumberOnLength(previousUnmasked, '');
				const currentMasked = UtilityService.maskPhoneNumberOnLength(numericValue, '');
				const previousMaskCount = previousMasked.slice(0, cursorPosition).replace(/\d/g, '').length;
				const previousUnmaskedPosition = cursorPosition - previousMaskCount;
				const currentDigitsBeforeCursor = numericValue.slice(0, previousUnmaskedPosition).length;
				let newCursorPosition = currentDigitsBeforeCursor;
				newCursorPosition += currentMasked.slice(0, newCursorPosition).replace(/\d/g, '').length;
				if (numericValue.length < previousUnmasked.length) {
					const targetPosition = currentMasked.lastIndexOf(numericValue[previousUnmaskedPosition - 1], newCursorPosition);
					if (targetPosition !== -1) {
						newCursorPosition = targetPosition + 1;
					} else {
						newCursorPosition = Math.max(0, cursorPosition - 1);
					}
				}
				if (numericValue.length > previousUnmasked.length) {
					newCursorPosition += 1;
				}
				newCursorPosition = Math.max(0, Math.min(newCursorPosition, currentMasked.length));
				setContactDetails({ ...contactDetails, [name]: numericValue });
				setTimeout(() => {
					input.value = currentMasked;
					input.setSelectionRange(newCursorPosition, newCursorPosition);
				}, 0);
			}
		} else {
			if (isNotReqired && value === "") {
				setErrors({ ...errors, [name]: "" });
			} else {
				validateForm({ [name]: value }, (Errors) => {
					setErrors({ ...errors, ...Errors });
				});
			}
			setContactDetails({ ...contactDetails, [name]: value });
		}
	};

	const handleCustomAttributeChange = (reference: string, value: string) => {
		setCustomAttributesValues((prev) => ({ ...prev, [reference]: value }));
		setErrorsCustomAttributes((prev) => ({ ...prev, [reference]: "" }));
	};

	const validateCustomAttributes = (): boolean => {
		const next: Record<string, string> = {};
		customAttributeFields.forEach((def) => {
			if (def.IsRequired) {
				const val = (customAttributesValues[def.Reference] ?? "").trim();
				if (!val) next[def.Reference] = `${def.Name} is required.`;
			}
		});
		setErrorsCustomAttributes(next);
		return Object.keys(next).length === 0;
	};

	const formSubmit = (event: any) => {
		event.preventDefault();
		const standardValid = validateForm(
			{ FirstName: contactDetails.FirstName, PhoneNumber: contactDetails.PhoneNumber, contactlastname: contactDetails.LastName, contactemail: contactDetails.Email },
			(Errors) => setErrors((prev) => ({ ...prev, ...Errors }))
		);
		const customValid = validateCustomAttributes();
		if (!standardValid || !customValid) {
			return;
		}
		setIsLoading(true)

		const payload = {
			id: contactDetails.ID,
			FirstName: contactDetails.FirstName,
			PhoneNumber: contactDetails.PhoneNumber,
			Lastname: contactDetails.LastName,
			Email: contactDetails.Email,
			groups: contactDetails.Groups.map((g) => g.ID),
			customAttributes: customAttributeFields
				.map((def) => ({
					id: def.Id,
					reference: def.Reference,
					name: def.Name,
					value: (customAttributesValues[def.Reference] || "").trim(),
				}))
				.filter((x) => x.value),
		};

		if (!!contactDetails.ID) {
			ContactService.updateContact(payload)
				.then(
					(res) => {
						let groups = Groups.filter((x: Group) => res.data.groups?.includes(x.ID))
						const contact = {
							...mapToContact(res.data),
							Groups: groups
						}
						setIsLoading(false)
						// UpdateContact(dispatch, contact);
						// LoadGroups(dispatch);
						setContactDetails({ ...contact });
						setCustomAttributesValues(customAttributesToValues(contact.CustomAttributes, customAttributeFields));
						setToast(ToasterTexts.CONTACT_EDIT_SUCCESS.replace('<Name>', (contact.Name)), ToasterType.SUCCESS)
						props.onClose()
						if (!!props?.clearSelection)
							props.clearSelection()
					}
				)
				.catch((err) => {
					setIsLoading(false)
					setToast(err.response?.data?.message, ToasterType.DANGER)
				})
		}
		else {
			ContactService.createContact(payload)
				.then((res) => {
					let groups = Groups.filter((x: Group) => res.data.groups?.includes(x.ID))

					const contact = {
						...mapToContact(res.data),
						Groups: groups
					}
					// UpdateContact(dispatch, contact);
					// LoadGroups(dispatch);
					setContactDetails({ ...contact });
					setCustomAttributesValues(customAttributesToValues(contact.CustomAttributes, customAttributeFields));
					setIsLoading(false)
					setToast(ToasterTexts.CONTACT_ADD_SUCCESS.replace('<Name>', (contact.Name)), ToasterType.SUCCESS)
					props.onClose()
					props.clearSelection()

				}
				)
				.catch((err) => {
					setIsLoading(false)
					if (err.response) {
						setToast(err.response.data.message, ToasterType.DANGER)
					}
				})
		}
	};

	const onGroupChange = (event: any, values: any) => {
		setContactDetails({ ...contactDetails, Groups: values })
	};
	const renderCustomAttributeField = (def: CustomAttributeDefinitionModel, isMobile: boolean) => {
		const value = customAttributesValues[def.Reference] ?? "";
		const error = errorsCustomAttributes[def.Reference] ?? "";
		const inputClasses = isMobile ? "h2 color-text-field-font" : "h4-regular color-text-field-font";
		const helperClass = isMobile ? "h5 color-primary-danger" : "h6-regular color-primary-danger";
		const placeholderText = (def.Description && def.Description.trim()) ? def.Description.trim() : def.Name;
		const selectPlaceholderText = (def.Description && def.Description.trim()) ? def.Description.trim() : `Select ${def.Name}`;


		if (def.Type === 0) {
			// Text
			return (
				<Grid item xs={12} key={def.Reference} className="mt-2">
					<TextField
						label={def.Name}
						placeholder={placeholderText}
						variant="outlined"
						size="small"
						fullWidth
						required={def.IsRequired}
						value={value}
						onChange={(e) => {
							handleCustomAttributeChange(def.Reference, e.target.value)
						}}
						InputProps={{ classes: { input: inputClasses } }}
						autoComplete="off"
					/>
					{error && <FormHelperText className={`${helperClass} t-align-right`}>{error}</FormHelperText>}
				</Grid>
			);
		}
		if (def.Type === 1) {
			// Number
			return (
				<Grid item xs={12} key={def.Reference} className="mt-2">
					<TextField
						label={def.Name}
						placeholder={placeholderText}
						type="text"
						inputMode="numeric"
						variant="outlined"
						size="small"
						fullWidth
						required={def.IsRequired}
						value={value}
						onChange={(e) => {
							const value = e.target.value;
							if (!/^\d*$/.test(value)) {
								return;
							}
							handleCustomAttributeChange(def.Reference, e.target.value)
						}}
						InputProps={{ classes: { input: inputClasses } }}
						autoComplete="off"
					/>
					{error && <FormHelperText className={`${helperClass} t-align-right`}>{error}</FormHelperText>}
				</Grid>
			);
		}
		if (def.Type === 2) {
			// Date
			const dateTimeNum = value ? new Date(value).getTime() : -1;
			return (
				<Grid item xs={12} key={def.Reference} className="mt-2">
					<DateAndTimePicker
						TextLabel={def.Name}
						DateTime={dateTimeNum}
						HideTime={true}
						FormatTextLabel={selectPlaceholderText}
						FormatText="MMMM Do YYYY"
						ShowMinData={true}
						handleChange={(ts: number) => {
							const dateStr = new Date(ts).toISOString().slice(0, 10);
							handleCustomAttributeChange(def.Reference, dateStr);
						}}
					/>
					{error && <FormHelperText className={`${helperClass} t-align-right`}>{error}</FormHelperText>}
				</Grid>
			);
		}
		if (def.Type === 3) {
			// Boolean
			const boolOptions = ["true", "false"];
			const selectedValue = value === "true" || value === "True" ? "true" : value === "false" || value === "False" ? "false" : null;
			return (
				<Grid item xs={12} key={def.Reference} className="mt-2">
					<Autocomplete
						options={boolOptions}
						value={selectedValue}
						onChange={(_, newValue) => handleCustomAttributeChange(def.Reference, newValue ?? "")}
						renderInput={(params) => (
							<TextField
								{...params}
								label={def.Name}
								placeholder={selectPlaceholderText}
								variant="outlined"
								size="small"
								fullWidth
								required={def.IsRequired}
								InputProps={{ ...params.InputProps, classes: { input: inputClasses } }}
							/>
						)}
					/>
					{error && <FormHelperText className={`${helperClass} t-align-right`}>{error}</FormHelperText>}
				</Grid>
			);
		}
		if (def.Type === 4) {
			const optionDetails = def.OptionsDetail ?? [];
			const selectedOption = optionDetails.find((opt) => opt.id === value) ?? null;

			return (
				<Grid item xs={12} key={def.Reference} className="mt-2">
					<Autocomplete
						multiple
						size="small"
						limitTags={1}
						className={"bg-color-secondary c-pointer"}
						options={optionDetails}
						value={selectedOption ? [selectedOption] : []}
						onChange={(_, newValue) => {
							if (!newValue?.length) {
								handleCustomAttributeChange(def.Reference, "");
								return;
							}
							const picked = newValue[newValue.length - 1];
							handleCustomAttributeChange(def.Reference, picked?.id ?? "");
						}}
						getOptionLabel={(option) => option.label ?? ""}
						isOptionEqualToValue={(option, selected) => option.id === selected.id}
						clearOnEscape
						renderTags={(tagValue, getTagProps) =>
							tagValue.map((option, index) => (
								<Chip
									classes={{
										colorPrimary: "custom-color-chip-2",
									}}
									color="primary"
									label={option.label ?? ""}
									{...getTagProps({ index })}
								/>
							))
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label={def.Name}
								placeholder={selectedOption ? "" : selectPlaceholderText}
								variant="outlined"
								size="small"
								fullWidth
								required={def.IsRequired}
								InputProps={{
									...params.InputProps,
									classes: { input: inputClasses },
								}}
							/>
						)}
					/>
					{error && (
						<FormHelperText className={`${helperClass} t-align-right`}>
							{error}
						</FormHelperText>
					)}
				</Grid>
			);
		}
		return null;
	};

	return (
		<div className="w-fill">
			{IsMobileDevice ?
				<div className={` ${IsDarkMode ? "bg-color-sidebar-bg" : "paper-background"} o-x-hidden w-fill h-v-100 bg-color-priary-background`}>
					<Grid item xs={12}>
						<div className='box-shadow-display-background-shadow d-flex p-2'>
							{IsMobileDevice && <span><BackMobileIcon className={"c-pointer pr-2 "} onClick={() => props.onClose()} /></span>}
							<span className={`h1 my-auto color-add-details-title mr-auto`}>{title}</span>
						</div>
					</Grid>
					<form
						noValidate
						autoComplete="no"
						onSubmit={formSubmit}
						className="m-2"
					>
						<Grid container direction="column" spacing={2}>
							<Grid item xs={12} className="mt-3">
								<TextField autoFocus
									name="FirstName"
									placeholder="First Name"
									label="First Name"
									variant="outlined"
									size="small"
									required
									fullWidth
									value={contactDetails.FirstName}
									onChange={(e) =>
										handleChange(e.target.name, e.target.value)
									}
									InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
									autoComplete='no'
								/>
								<FormHelperText className="h5 color-primary-danger t-align-right">
									{errors.FirstName}
								</FormHelperText>
							</Grid>
							<Grid item xs={12} className="mt-1">
								<TextField
									placeholder="Last Name"
									name="LastName"
									label="Last Name"
									variant="outlined"
									size="small"
									fullWidth
									value={contactDetails.LastName}
									onChange={(e) =>
										handleChange(e.target.name, e.target.value, true)
									}
									InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
									autoComplete='no'
								/>
								<FormHelperText className="h5 color-primary-danger t-align-right">
									{errors.LastName}
								</FormHelperText>
							</Grid>
							<Grid item xs={12}>
								<TextField
									id="maskExample"
									label="Phone Number"
									name="PhoneNumber"
									className="mt-1"
									variant='outlined'
									required
									fullWidth
									size='small'
									InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
									value={UtilityService.maskPhoneNumberOnLength(contactDetails.PhoneNumber, '')}
									onChange={(e) => handleChange(e.target.name, e.target.value)}
									autoComplete='no'
									inputRef={inputRef}
								/>
								<FormHelperText className="h5 color-primary-danger t-align-right">
									{errors.PhoneNumber}
								</FormHelperText>
							</Grid>
							<Grid item xs={12}>
								<TextField
									fullWidth
									label="Email"
									name="Email"
									placeholder="Email"
									size="small"
									variant="outlined"
									value={contactDetails.Email}
									onChange={(e) =>
										handleChange(e.target.name, e.target.value, true)
									}
									InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
									className="mt-1"
									autoComplete='no'
								/>
								<FormHelperText className="h5 color-primary-danger t-align-right">
									{errors.Email}
								</FormHelperText>
							</Grid>
							{customAttributeFields.map((def) => renderCustomAttributeField(def, true))}
							<Grid item xs={12}>
								<Autocomplete
									multiple
									className={"mt-1 w-p-100 p-0"}
									options={Groups}
									getOptionLabel={option => option.Name}
									value={contactDetails.Groups || []}
									onChange={onGroupChange}
									getOptionDisabled={(option) => (!!contactDetails.Groups && contactDetails.Groups?.findIndex(x => x.ID === option.ID) !== -1) || option.ContactsCount >= groupContactCount}
									filterSelectedOptions
									// getOptionSelected={(option: Group, value: Group) => option.ID === value.ID}
									classes={{
										option: "custom-option",
										groupLabel: "custom-group-label",
										groupUl: "m-1",
										input: "h-5 color-primary-dark h2 p-0 ",
									}}
									renderOption={(defaultprops, option, { selected }) => (
										<li {...defaultprops} className={`${defaultprops.className} c-pointer h2 color-menu-list-font-color p-2 d-flex justify-between ${selected ? 'Mui-focused' : ''}`}>
											<div>{!!option.Name ? UtilityService.trimMessage(option.Name, 13) : option.Name}</div>
											<div className="badge-label">
												{option.ContactsCount}
											</div>
										</li>
									)}
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip
												avatar={<Avatar> {option.ContactsCount}</Avatar>}
												classes={{
													colorPrimary: "custom-color-chip-2",
													avatarColorPrimary: "custom-chip-avatar",
												}}
												color='primary'
												label={!!option.Name ? UtilityService.trimMessage(option.Name, 13) : option.Name}
												{...getTagProps({ index })}
											/>
										))
									}
									renderInput={params => (
										<TextField
											{...params}
											label="Groups"
											variant='outlined'
											placeholder=""
											inputProps={{ ...params.inputProps, autoComplete: 'no' }}
										/>
									)}
								/>
							</Grid>
							<div className="h5-regular mt-2 d-flex align-center ml-2 br-1 py-1 b-around-consent-message-border color-button-text bg-color-consent-message-bg">
								<InfoCircleIcon className="w-p-10" />
								<span>By clicking Save, you confirm that this contact has given their consent to receive SMS messages from you.</span>
							</div>
						</Grid>
					</form>
					<div className="d-flex m-2 mt-auto">
						<ButtonComponent OnClick={() => props.onClose()} ButtonLabel={buttonLabels.CANCEL} ClassName={"mr-1 ml-auto"} Variant={"secondary-btn-small"} />
						<ButtonComponent OnClick={formSubmit} ButtonLabel={buttonLabels.SAVE} Loading={IsLoading} ClassName={"px-4"} Variant={"primary-btn-small"} />
					</div>
				</div >
				: <Dialog
					maxWidth="sm"
					fullWidth={true}
					aria-labelledby="alert-dialog-title"
					aria-describedby="alert-dialog-description"
					classes={{ paper: `w-66 br-2 ${IsDarkMode ? "" : "dialogue-shadow"}` }}
					BackdropProps={{ style: { backgroundColor: "transparent" } }}
					open={props.show}
					// onClose={() => props.onClose()}
					disableEscapeKeyDown
				>
					<DialogTitle id="alert-dialog-title" className={`p-2 ${IsDarkMode ? "bg-color-sidebar-bg" : ""}`}>
						<div className='d-flex'>
							<span className={`h2-regular ${IsDarkMode ? "" : "color-text-field-font"}`}>{title}</span>
							<Tooltip title='Close' placement='bottom'>
								<CancelIcon className={"c-pointer ml-auto"} onClick={() => props.onClose()} />
							</Tooltip>
						</div>
					</DialogTitle>
					<div className={"bb-primary-background-pink-4 mx-2"}></div>
					<DialogContent className={`pt-2 px-2 pb-0 o-x-hidden ${IsDarkMode ? "bg-color-sidebar-bg" : ""}`}>
						<form
							noValidate
							autoComplete="off"
							onSubmit={formSubmit}
							className="m-auto"
						>
							<Grid container direction="row" spacing={2}>
								<Grid item xs={6} className="">
									<TextField autoFocus
										name="FirstName"
										placeholder="First Name"
										label="First Name"
										variant="outlined"
										size="small"
										required
										fullWidth
										value={contactDetails.FirstName}
										onChange={(e) => handleChange(e.target.name, e.target.value)}
										InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
										autoComplete='no'
									/>
									<FormHelperText className="h6-regular color-primary-danger t-align-right">
										{errors.FirstName}
									</FormHelperText>
								</Grid>
								<Grid item xs={6} className="">
									<TextField
										placeholder="Last Name"
										name="LastName"
										label="Last Name"
										variant="outlined"
										size="small"
										fullWidth
										value={contactDetails.LastName}
										onChange={(e) =>
											handleChange(e.target.name, e.target.value, true)
										}
										InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
										autoComplete='no'
									/>
									<FormHelperText className="h6-regular color-primary-danger t-align-right">
										{errors.LastName}
									</FormHelperText>
								</Grid>
							</Grid>
							<Grid container direction="row" spacing={2}>
								<Grid item xs={6}>
									<TextField
										id="maskExample"
										label="Phone Number"
										name="PhoneNumber"
										className="mt-1 color-primary-dark h6"
										variant='outlined'
										required
										fullWidth
										size='small'
										InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
										value={UtilityService.maskPhoneNumberOnLength(contactDetails.PhoneNumber, '')}
										onChange={(e) => handleChange(e.target.name, e.target.value)}
										onKeyDown={(e) => {
											// if (/[a-zA-Z]/.test(e.key)) {
											// 	e.preventDefault();
											// }
										}}
										autoComplete='no'
										inputRef={inputRef}
									/>
									<FormHelperText className=" h6-regular color-primary-danger t-align-right">
										{errors.PhoneNumber}
									</FormHelperText>
								</Grid>
								<Grid item xs={6}>
									<TextField
										fullWidth
										label="Email"
										name="Email"
										placeholder="Email"
										size="small"
										variant="outlined"
										value={contactDetails.Email}
										onChange={(e) =>
											handleChange(e.target.name, e.target.value, true)
										}
										InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
										className="mt-1"
										autoComplete='no'
									/>
									<FormHelperText className="h6-regular color-primary-danger t-align-right">
										{errors.Email}
									</FormHelperText>
								</Grid>
							</Grid>
							{customAttributeFields.map((def) => renderCustomAttributeField(def, false))}
							<Grid item xs={12}>
								<Autocomplete
									multiple
									className={"mt-2"}
									options={Groups}
									getOptionLabel={option => option.Name}
									value={contactDetails.Groups || []}
									onChange={onGroupChange}
									getOptionDisabled={(option) => (!!contactDetails.Groups && contactDetails.Groups?.findIndex(x => x.ID === option.ID) !== -1) || option.ContactsCount >= groupContactCount}
									filterSelectedOptions
									// getOptionSelected={(option: Group, value: Group) => option.ID === value.ID}
									classes={{
										option: "custom-option ",
										groupLabel: "custom-group-label ",
										groupUl: "m-1",
										input: "h-6 color-senary h4-regular"
									}}
									renderOption={(defaultprops, option, { selected }) => (
										<li {...defaultprops} className={`${defaultprops.className} c-pointer h4-regular font-bold p-2 d-flex justify-between ${selected ? 'Mui-focused' : ''}`}>
											<div>
												{option.Name}
											</div>
											<div className="badge-label">
												{option.ContactsCount}
											</div>
										</li>
									)}
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip
												avatar={<Avatar> {option.ContactsCount}</Avatar>}
												classes={{
													colorPrimary: "custom-color-chip-2",
													avatarColorPrimary: "custom-chip-avatar",
												}}
												color='primary'
												label={option.Name}
												{...getTagProps({ index })}
											/>
										))
									}
									renderInput={params => (
										<TextField
											{...params}
											label="Groups"
											variant='outlined'
											placeholder=""
											inputProps={{ ...params.inputProps, autoComplete: 'no' }}
										/>
									)}
								/>
							</Grid>
							<div className="h5-regular mt-2 d-flex align-center br-2 py-1 b-around-consent-message-border color-button-text bg-color-consent-message-bg">
								<InfoCircleIcon className="w-p-10" />
								<span>By clicking Save, you confirm that this contact has given their consent to receive SMS messages from you.</span>
							</div>
						</form>
					</DialogContent>
					<DialogActions className={`pb-2 px-2 ${IsDarkMode ? " bg-color-sidebar-bg" : ""}`}>
						<ButtonComponent OnClick={() => props.onClose()} ButtonLabel={buttonLabels.CANCEL} ClassName={""} Variant={"secondary-btn-small"} />
						<ButtonComponent OnClick={formSubmit} ButtonLabel={buttonLabels.SAVE} Loading={IsLoading} ClassName={"px-4"} Variant={"primary-btn-small"} />
					</DialogActions>
				</Dialog>
			}
		</div >
	)
}


export default ContactViewComponent
